import { Injectable, OnDestroy } from "@angular/core";
import { AppConfig } from "src/app/app.config";
import { AuthenticationClient } from "src/app/system/auth/authentication.client";
import dayjs from 'dayjs'; 
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TokenManager implements OnDestroy {
  constructor(
    private authClient: AuthenticationClient,
    private config: AppConfig,
    private router: Router,
    private toastr: ToastrService
  ) {
  }

  private readonly destroy$ = new Subject<void>();

  /* 
        note : check once after 7.5 hours for 8-hour session
               if browser window is inactive or in background, interval may be throttled down
    */
  interval: number = 12 * 60 * 60 * 1000; //check in 12 hours
  intervalRef: any;
  renewsRemaining: number = 1; // number of checks to do, until stopping

  // renewal delay values
  ngRenewalDelayMinutes: number = 720; // 12 hours (720 minutes)
  stopRenewalRequests() {
    clearInterval(this.intervalRef);
    this.intervalRef = null;
    this.renewsRemaining = 0;
    localStorage.removeItem('ngRenewAt');
  }

  startRenewalRequests() {
    localStorage.removeItem('ngRenewAt');
    this.continueRenewalRequests();
  }

  continueRenewalRequests() {
    if (
      localStorage.getItem('user') == null ||
      localStorage.getItem('auth') == null
    )
      return;

    this.renewsRemaining = 2; // 2 more renews

    this.tryRenewTokens(true);
    if (this.intervalRef == null && this.renewsRemaining > 0) {
      this.intervalRef = setInterval(() => {
        this.tryRenewTokens();
      }, this.interval);
    }
  }


  tryCheckUpdateSession(skipCountdown: boolean = false): void {
    if (
      localStorage.getItem('user') == null ||
      localStorage.getItem('auth') == null
    ) {
      return;
    }

    // refresh ngToken
    let currentUser = localStorage.getItem('user');
    let currentNgToken = localStorage.getItem('auth');
    this.authClient
      .checkUpdateSession(currentNgToken, currentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res == 'LOCKED') {
          //force logout
          this.toastr.error('User account is locked or invalid');
          this.router.navigate(['auth', 'logout']);
          return;
        } 
      });

    if (skipCountdown) return;
  }

  tryRenewTokens(skipCountdown: boolean = false): void {
    if (
      localStorage.getItem('user') == (null ) ||   
      localStorage.getItem('auth') == (null )
    )
      return;

    // refresh ngToken
    if (this.shouldRenewNgToken()) {
      let currentUser = localStorage.getItem('user');
      let currentNgToken = localStorage.getItem('auth');
      this.authClient
        .refreshNgAuthToken(currentNgToken, currentUser)
        .pipe(takeUntil(this.destroy$))
        .subscribe((ngToken) => {
          if (ngToken == 'LOCKED') {
            //force logout
            this.toastr.error('User account is locked or invalid');
            this.router.navigate(['auth', 'logout']);
            return;
          }
          localStorage.setItem('auth', ngToken);
          let newNgRenewAtTicks = dayjs(new Date())
            .add(this.ngRenewalDelayMinutes, 'minutes')
            .toDate()
            .getTime();
          localStorage.setItem('ngRenewAt', newNgRenewAtTicks.toString());
        });
    }

    if (skipCountdown) return;

    this.renewsRemaining--;
    if (this.renewsRemaining == 0) {
      clearInterval(this.intervalRef);
      this.intervalRef = null;
    }
  }

  setupTokenRenewTimes() {
    // ng token renewal time - use closest renew time - delay minutes out or sooner
    let newNgRenewAtTicks = dayjs(new Date())
      .add(this.ngRenewalDelayMinutes, 'minutes')
      .toDate()
      .getTime();
    if (localStorage.getItem('ngRenewAt') == null) {
      localStorage.setItem('ngRenewAt', newNgRenewAtTicks.toString());
    } else {
      let lsiNgRenewAtTicks = localStorage.getItem('ngRenewAt');
      localStorage.setItem(
        'ngRenewAt',
        Math.min(newNgRenewAtTicks, +lsiNgRenewAtTicks).toString()
      );
    }
  }

  shouldRenewNgToken(): boolean {
    let time = new Date(+localStorage.getItem('ngRenewAt'));
    return time < new Date();
  }

  sendMessageAfterIFrameLoad(sendMessageCallback) {
    if (!this._isIframeReady) {
      // console.log('waiting for iframe');
      window.setTimeout(
        () => this.sendMessageAfterIFrameLoad(sendMessageCallback),
        300
      );
    } else {
      // note : each message sent causes the iframe content to re-load, due to .aspx postback & internal page logic
      this._isIframeReady = false;
      sendMessageCallback();
    }
  }
  _isIframeReady = false;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopRenewalRequests();
  }
}
